import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import styles from './AnexarFotoProduto.module.css';

// Foto anexada no Detalhe do produto da Loja Física (issue #1).
// Prioridade na tela: anexada > Bling > foto antiga > logo GRINGÃO. Vale para Centro e Caxias.
const LIMITE_MB = 15;

// O Detalhe sai do servidor com cache de 10 min no navegador. Quem mexeu na foto nesta aba
// busca com ?v=<versão> para ver a foto nova na hora (os outros veem em até 10 min).
const chaveVersao = (codigo) => `foto-versao-${codigo}`;
export const versaoFoto = (codigo) => {
  try { return sessionStorage.getItem(chaveVersao(codigo)) || ''; } catch (_) { return ''; }
};
const marcarVersao = (codigo) => {
  try { sessionStorage.setItem(chaveVersao(codigo), String(Date.now())); } catch (_) { /* aba anônima */ }
};

// Troca a imagem do produto em qualquer dado já carregado (Detalhe e grades), sem buscar de novo.
function trocarImagemNosDados(dado, codigo, imagem, profundidade = 0) {
  if (!dado || typeof dado !== 'object' || profundidade > 4) return dado;
  if (Array.isArray(dado)) {
    let mudou = false;
    const novo = dado.map((it) => {
      const r = trocarImagemNosDados(it, codigo, imagem, profundidade + 1);
      if (r !== it) mudou = true;
      return r;
    });
    return mudou ? novo : dado;
  }
  if (String(dado.codigoInterno ?? '') === String(codigo) && 'imagemBase64' in dado) {
    return { ...dado, imagemBase64: imagem };
  }
  let mudou = false;
  const novo = { ...dado };
  for (const k of Object.keys(dado)) {
    const r = trocarImagemNosDados(dado[k], codigo, imagem, profundidade + 1);
    if (r !== dado[k]) { novo[k] = r; mudou = true; }
  }
  return mudou ? novo : dado;
}

const fmtData = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
};

const AnexarFotoProduto = ({ codigo }) => {
  const queryClient = useQueryClient();
  const inputRef = useRef(null);
  const [previa, setPrevia] = useState(null); // { arquivo, url }
  const [enviando, setEnviando] = useState(false);
  const [mensagem, setMensagem] = useState(null); // { tipo: 'ok'|'erro', texto }

  const { data: foto } = useQuery({
    queryKey: ['foto-anexada', codigo],
    queryFn: async () => (await api.get(`/api/produtos/loja-fisica/${codigo}/foto`)).data.foto,
    enabled: !!codigo,
  });

  // Sem refazer a busca do Detalhe/grades: ela viria do cache do navegador com a foto velha.
  const atualizarTelas = (imagem) => {
    marcarVersao(codigo);
    queryClient.invalidateQueries({ queryKey: ['foto-anexada', codigo] });
    queryClient.setQueriesData(
      { predicate: (q) => { const k = String(q.queryKey?.[0] || ''); return k === 'produto-detalhe' || k.includes('loja'); } },
      (antigo) => trocarImagemNosDados(antigo, codigo, imagem ?? null),
    );
  };

  const escolher = (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    setMensagem(null);
    if (!arquivo) return;
    if (!arquivo.type.startsWith('image/')) return setMensagem({ tipo: 'erro', texto: 'Escolha um arquivo de imagem (JPG, PNG ou WEBP).' });
    if (arquivo.size > LIMITE_MB * 1024 * 1024) return setMensagem({ tipo: 'erro', texto: `A foto pode ter no máximo ${LIMITE_MB} MB.` });
    if (previa) URL.revokeObjectURL(previa.url);
    setPrevia({ arquivo, url: URL.createObjectURL(arquivo) });
  };

  const cancelar = () => {
    if (previa) URL.revokeObjectURL(previa.url);
    setPrevia(null);
  };

  const salvar = async () => {
    if (!previa) return;
    setEnviando(true);
    setMensagem(null);
    try {
      const fd = new FormData();
      fd.append('foto', previa.arquivo);
      // o api manda JSON por padrão; o axios completa o boundary do multipart
      const { data: resp } = await api.post(`/api/produtos/loja-fisica/${codigo}/foto`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      cancelar();
      setMensagem({ tipo: 'ok', texto: 'Foto salva! Ela já vale para todo o sistema.' });
      atualizarTelas(resp?.imagem || resp?.url);
    } catch (err) {
      setMensagem({ tipo: 'erro', texto: err.response?.data?.message || err.message || 'Não foi possível salvar a foto agora.' });
    } finally {
      setEnviando(false);
    }
  };

  const remover = async () => {
    if (!window.confirm('Remover a foto anexada? O produto volta a mostrar a foto do Bling (ou a antiga).')) return;
    setEnviando(true);
    setMensagem(null);
    try {
      const { data: resp } = await api.delete(`/api/produtos/loja-fisica/${codigo}/foto`);
      setMensagem({ tipo: 'ok', texto: 'Foto anexada removida.' });
      atualizarTelas(resp?.imagem);
    } catch (err) {
      setMensagem({ tipo: 'erro', texto: err.response?.data?.message || err.message || 'Não foi possível remover agora.' });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className={styles.caixa}>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/*" onChange={escolher} hidden />

      {previa ? (
        <div className={styles.previa}>
          <img src={previa.url} alt="Prévia da foto" />
          <div className={styles.acoes}>
            <button type="button" className={styles.principal} onClick={salvar} disabled={enviando}>
              {enviando ? 'Salvando…' : '✓ Usar esta foto'}
            </button>
            <button type="button" className={styles.secundario} onClick={cancelar} disabled={enviando}>Cancelar</button>
          </div>
        </div>
      ) : (
        <div className={styles.acoes}>
          <button type="button" className={styles.principal} onClick={() => inputRef.current?.click()} disabled={enviando}>
            📷 {foto ? 'Trocar foto' : 'Anexar foto'}
          </button>
          {foto && (
            <button type="button" className={styles.secundario} onClick={remover} disabled={enviando}>Remover</button>
          )}
        </div>
      )}

      {foto && !previa && (
        <div className={styles.selo}>Foto anexada por {foto.anexadaPor}{fmtData(foto.anexadaEm) ? ` em ${fmtData(foto.anexadaEm)}` : ''}</div>
      )}
      {mensagem && <div className={mensagem.tipo === 'ok' ? styles.ok : styles.erro}>{mensagem.texto}</div>}
    </div>
  );
};

export default AnexarFotoProduto;
